/**
 * PeriodToggle — YTD / Quartal / Monat / Datum-Toggle + Jahr-Auswahl.
 * #1184 v2.569.0: Pfeil-Buttons ‹ › für Monats-Navigation, dezenter Reife-Indikator
 *   am Monats-Dropdown (✅ belastbar / ⏳ vorläufig / 🔴 unvollständig).
 * Datum-Modus (#1141 v2.497.0 B3): 2× ISO-Date-Picker, Live-Odoo-Compute statt
 * gespeicherter Monatswerte. Hinweis-Banner wird im Eltern-Container gerendert.
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { monthMaturity } from '../../../utils/data-maturity';

const REIFE_MARK = {
  belastbar: { dot: '#10B981', label: 'belastbar' },
  vorlaeufig: { dot: '#F59E0B', label: 'vorläufig' },
  unvollstaendig: { dot: '#EF4444', label: 'unvollständig' },
};

export default function PeriodToggle({ period, year, month, quarter, fromDate, toDate, onChange }) {
  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2];
  const monthLabels = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

  function set(key, value) {
    const next = { period, year, month, quarter, fromDate, toDate, [key]: value };
    if (key === 'period') {
      if (value === 'month' && !month) next.month = new Date().getMonth() + 1;
      if (value === 'quarter' && !quarter) next.quarter = Math.ceil((new Date().getMonth() + 1) / 3);
      if (value === 'custom') {
        const today = new Date();
        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, '0');
        const d = String(today.getDate()).padStart(2, '0');
        if (!next.fromDate) next.fromDate = `${y}-${m}-01`;
        if (!next.toDate) next.toDate = `${y}-${m}-${d}`;
      }
    }
    onChange(next);
  }

  /** Pfeil-Navigation: clamp 1–12, Jahr wrappt. */
  function stepMonth(direction) {
    let nextMonth = (month || 1) + direction;
    let nextYear = year;
    if (nextMonth < 1) { nextMonth = 12; nextYear -= 1; }
    if (nextMonth > 12) { nextMonth = 1; nextYear += 1; }
    onChange({ period, year: nextYear, month: nextMonth, quarter, fromDate, toDate });
  }

  const showYearSelect = period !== 'custom';
  const reife = (period === 'month' && month) ? monthMaturity(year, month) : null;
  const reifeMark = reife ? REIFE_MARK[reife] : null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="bg-[#F8F9FC] rounded-[10px] p-[3px] border border-[#E5E7EB] flex items-center gap-0.5">
        {['ytd', 'quarter', 'month', 'custom'].map(p => (
          <button
            key={p}
            onClick={() => set('period', p)}
            className={`px-3 py-1 text-xs font-medium rounded-md ${
              period === p ? 'bg-white text-[#1A1A2E] shadow-sm' : 'text-[#6B7280] hover:text-[#1A1A2E]'
            }`}
          >
            {p === 'ytd' ? 'YTD' : p === 'quarter' ? 'Quartal' : p === 'month' ? 'Monat' : 'Datum'}
          </button>
        ))}
      </div>

      {period === 'month' && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => stepMonth(-1)}
            className="w-7 h-7 rounded-md border border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-[#F8F9FC] hover:text-[#1A1A2E] flex items-center justify-center"
            aria-label="Vorheriger Monat"
            title="Vorheriger Monat"
          >
            <ChevronLeft width={14} height={14} strokeWidth={2} />
          </button>
          <select
            value={month || 1}
            onChange={e => set('month', parseInt(e.target.value, 10))}
            className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white"
          >
            {monthLabels.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
          <button
            type="button"
            onClick={() => stepMonth(+1)}
            className="w-7 h-7 rounded-md border border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-[#F8F9FC] hover:text-[#1A1A2E] flex items-center justify-center"
            aria-label="Nächster Monat"
            title="Nächster Monat"
          >
            <ChevronRight width={14} height={14} strokeWidth={2} />
          </button>
          {reifeMark && (
            <span
              className="inline-flex items-center gap-1 text-[10px] text-[#6B7280] pl-1"
              title={`Datenreife: ${reifeMark.label}`}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: reifeMark.dot }} />
              {reifeMark.label}
            </span>
          )}
        </div>
      )}
      {period === 'quarter' && (
        <select
          value={quarter || 1}
          onChange={e => set('quarter', parseInt(e.target.value, 10))}
          className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white"
        >
          {[1, 2, 3, 4].map(q => <option key={q} value={q}>Q{q}</option>)}
        </select>
      )}
      {period === 'custom' && (
        <>
          <input
            type="date"
            value={fromDate || ''}
            max={toDate || undefined}
            onChange={e => set('fromDate', e.target.value)}
            className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white"
          />
          <span className="text-xs text-[#9CA3AF]">–</span>
          <input
            type="date"
            value={toDate || ''}
            min={fromDate || undefined}
            onChange={e => set('toDate', e.target.value)}
            className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white"
          />
        </>
      )}
      {showYearSelect && (
        <select
          value={year}
          onChange={e => set('year', parseInt(e.target.value, 10))}
          className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white"
        >
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      )}
    </div>
  );
}

export function buildPeriodQuery({ period, year, month, quarter, fromDate, toDate }) {
  if (period === 'custom' && fromDate && toDate) {
    const p = new URLSearchParams({ from_date: fromDate, to_date: toDate });
    return p.toString();
  }
  const p = new URLSearchParams({ period, year: String(year) });
  if (period === 'month' && month) p.set('month', String(month));
  if (period === 'quarter' && quarter) p.set('quarter', String(quarter));
  return p.toString();
}

export function isCustomPeriod(periodState) {
  return periodState && periodState.period === 'custom' && periodState.fromDate && periodState.toDate;
}
